// Tiny inline-markdown parser. Supports a deliberately small surface:
//
//   **bold**         → <strong>
//   *italic*         → <em>
//   _italic_         → <em>
//   __underline__    → <u>
//   [text](url)      → <a href="url" …>  (only safe schemes)
//
// Everything else is escaped. URL safety: http(s)/mailto/anchors/relative
// only — `javascript:` and friends are silently rewritten to `#`. Used for
// both the WYSIWYG preview and the exported email HTML, so it must stay
// pure (no React, no DOM). The React wrapper lives in
// src/client/editor/inline-md.tsx.

const ESCAPE_RE = /[&<>"']/g;
const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
function esc(s: string): string {
  return s.replace(ESCAPE_RE, (c) => ESCAPE_MAP[c]);
}

function isSafeUrl(url: string): boolean {
  const u = url.trim().toLowerCase();
  if (u.startsWith('http://') || u.startsWith('https://')) return true;
  if (u.startsWith('mailto:') || u.startsWith('tel:')) return true;
  if (u.startsWith('/') || u.startsWith('#')) return true;
  return false;
}

const LINK_STYLE = 'color:#153EA7; text-decoration:underline;';

export function inlineMdToHtml(input: string): string {
  if (!input) return '';
  let out = esc(input);

  // Links FIRST so the inner text can carry its own marks (bold inside a link
  // anchor, etc.) without our subsequent passes corrupting the URL.
  out = out.replace(/\[([^\]\n]+?)\]\(([^)\s]+)\)/g, (_m, text: string, url: string) => {
    const safe = isSafeUrl(url) ? url : '#';
    return `<a href="${esc(safe)}" style="${LINK_STYLE}">${text}</a>`;
  });

  // Bold MUST run before single-star italic.
  out = out.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  // Underline before single-underscore italic.
  out = out.replace(/__([^_\n]+?)__/g, '<u>$1</u>');
  // Italic with *…* — the surrounding lookarounds keep us from catching the
  // leftover edges of `**bold**` we just consumed (defense in depth).
  out = out.replace(/(?<![*\w])\*([^*\n]+?)\*(?![*\w])/g, '<em>$1</em>');
  // Italic with _…_ — same deal.
  out = out.replace(/(?<![_\w])_([^_\n]+?)_(?![_\w])/g, '<em>$1</em>');

  // Newlines → <br>. Two-or-more newlines become a paragraph-style break
  // (two <br>s) so users can split prose into visual paragraphs without us
  // needing actual <p> blocks (which would break the surrounding template).
  out = out.replace(/\n{2,}/g, '<br/><br/>').replace(/\n/g, '<br/>');

  return out;
}
